import { useId } from '@radix-ui/react-id';
import { useControllableState } from '@radix-ui/react-use-controllable-state';
import { type ComponentPropsWithoutRef, memo, type ReactNode, useCallback, useRef } from 'react';

import { PANEL_NAME, PanelProvider, type ScopeProps } from '~/components/v2/components/shared/panel/PanelProvider';

export type PanelProps = ComponentPropsWithoutRef<'div'> & {
  children: ReactNode;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
};

export const Panel = memo(
  ({ __scopePanel, children, defaultOpen, open: openProp, onOpenChange }: ScopeProps<PanelProps>) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const [open, setOpen] = useControllableState({
      prop: openProp,
      defaultProp: defaultOpen ?? false,
      onChange: onOpenChange,
      caller: PANEL_NAME,
    });

    const onOpenClose = useCallback(() => {
      setOpen(false);
    }, [setOpen]);

    const onOpenToggle = useCallback(() => {
      setOpen((prev) => !prev);
    }, [setOpen]);

    return (
      <PanelProvider
        contentId={useId()}
        contentRef={contentRef}
        onOpenChange={setOpen}
        onOpenClose={onOpenClose}
        onOpenToggle={onOpenToggle}
        open={open}
        scope={__scopePanel}
        triggerRef={triggerRef}
      >
        {children}
      </PanelProvider>
    );
  },
);

Panel.displayName = 'Panel';
